import { isNil, omitBy } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';

import { ordersApi, CancelFeedback } from 'api';
import { PaymentLayoutAction, useSciDomain } from 'components';
import { EcomCardData } from 'components/EcomCardForm';
import {
  CustomerConfirmStatusDetails,
  EventName,
  NewStatusDetails,
  OrderStatus,
  PaymentType,
} from 'enums';
import { Order, PaymentFormValues, TradeMethod, UpdateOrderDto } from 'types';
import { eventUtils } from 'utils';

import { ApiActionOptions } from './useOrderData';

export type OrderViewActions = {
  cancel: PaymentLayoutAction;
  confirmCard: PaymentLayoutAction;
  confirmPayment: PaymentLayoutAction;
  backToShop: PaymentLayoutAction;
  selectTradeMethod: PaymentLayoutAction;
};

type UseOrderActionsInput = {
  orderId: string;
  order: Order | undefined;
  initialOrderFetched: boolean;
  paymentFormValues: PaymentFormValues;
  isCustomerDataConfirmed: boolean;
  customerDataBeforePayment: boolean;
  isCardInfoConfirmDisabled: boolean;
  setUploadReceiptModalOpen: (open: boolean) => void;
  setStartPaymentSyncFailed: (failed: boolean) => void;
  apiActionOptions: ApiActionOptions;
  actionLabels: Record<keyof OrderViewActions, string>;
};

export type UseOrderActionsResult = {
  actions: OrderViewActions;
  selectPaymentType: (tradeMethod: TradeMethod) => void;
  confirmCard: (
    formValues: PaymentFormValues,
    options?: { silent?: boolean },
  ) => void;
  confirmPayment: (orderId: string) => void;
  submitEcomCard: (card: EcomCardData) => void;
  submitEcomConfirmCode: (confirmCode: string) => void;
  isStartPaymentSyncAPICallInProgress: boolean;
  isStartRequisitesCascadeLoading: boolean;
  isEcomSubmitInProgress: boolean;
  isCancelLoading: boolean;
  cancelWithFeedback: (feedback?: CancelFeedback) => void;
};

export const useOrderActions = ({
  orderId,
  order,
  initialOrderFetched,
  paymentFormValues,
  isCustomerDataConfirmed,
  customerDataBeforePayment,
  isCardInfoConfirmDisabled,
  setUploadReceiptModalOpen,
  setStartPaymentSyncFailed,
  apiActionOptions,
  actionLabels,
}: UseOrderActionsInput): UseOrderActionsResult => {
  const { data: sciDomainConfig } = useSciDomain();

  const { mutate: update } = useMutation(ordersApi.update, apiActionOptions);
  const {
    mutate: startPaymentSync,
    isLoading: isStartPaymentSyncAPICallInProgress,
  } = useMutation(ordersApi.startPaymentSync, apiActionOptions);
  const {
    mutate: startRequisitesCascade,
    isLoading: isStartRequisitesCascadeLoading,
  } = useMutation(ordersApi.startRequisitesCascade, apiActionOptions);

  const requisitesCascadeStarted = React.useRef(false);
  const startPaymentLaunched = React.useRef(false);

  useEffect(() => {
    // start finding requisites if the order was created with specified payment type
    if (
      !requisitesCascadeStarted.current &&
      !startPaymentLaunched.current &&
      initialOrderFetched &&
      order?.status === OrderStatus.New &&
      order?.payment?.type &&
      // ecom starts payment sync only after the customer submits the card (submitEcomCard); it must not
      // auto-cascade requisites while the order is New, or it would leave New before the card is entered.
      order?.payment?.type !== PaymentType.Ecom &&
      // exception for RosaOne integration which finds requisites for order in New status
      order?.statusDetails !== NewStatusDetails.NewStartPaymentSyncAttempt &&
      !order?.requisites
    ) {
      requisitesCascadeStarted.current = true;
      startRequisitesCascade(orderId);
    }
  }, [
    initialOrderFetched,
    order?.status,
    order?.payment?.type,
    order?.requisites,
    orderId,
    sciDomainConfig?.template,
    startRequisitesCascade,
    order?.statusDetails,
  ]);

  useEffect(() => {
    // resetTradeMethod puts the order back in New so the customer can pick a different trade method,
    // but these refs are one-shot latches from the first attempt and won't reset themselves — without
    // this, the guard above would stay permanently blocked and the retry would never auto-cascade.
    if (order?.statusDetails === NewStatusDetails.NewTradeMethodReset) {
      requisitesCascadeStarted.current = false;
      startPaymentLaunched.current = false;
    }
  }, [order?.statusDetails]);

  const { mutate: startPayment } = useMutation(
    ordersApi.startPayment,
    apiActionOptions,
  );
  const { mutate: confirmPayment } = useMutation(
    ordersApi.confirmPayment,
    apiActionOptions,
  );
  const { mutate: cancelMutate, isLoading: isCancelLoading } = useMutation(
    ({ id, feedback }: { id: string; feedback?: CancelFeedback }) =>
      ordersApi.cancel(id, feedback),
    apiActionOptions,
  );

  const {
    mutate: submitConfirmCodeMutate,
    isLoading: isSubmitConfirmCodeLoading,
  } = useMutation(ordersApi.submitConfirmCode, apiActionOptions);

  const submitEcomCard = useCallback(
    (card: EcomCardData) =>
      startPaymentSync(
        { id: orderId, data: { card } },
        {
          onError: (error: any) => {
            eventUtils.trackException({
              message: `submitEcomCard failed. ${
                error?.response?.data?.message || error?.message || ''
              }`,
            });
          },
        },
      ),
    [orderId, startPaymentSync],
  );

  const submitEcomConfirmCode = useCallback(
    (confirmCode: string) =>
      submitConfirmCodeMutate(
        { id: orderId, confirmCode },
        {
          onError: (error: any) => {
            eventUtils.trackException({
              message: `submitConfirmCode failed. ${
                error?.response?.data?.message || error?.message || ''
              }`,
            });
          },
        },
      ),
    [orderId, submitConfirmCodeMutate],
  );

  const isEcomSubmitInProgress =
    isStartPaymentSyncAPICallInProgress || isSubmitConfirmCodeLoading;

  const status = useMemo(() => order?.status as OrderStatus, [order]);
  const returnUrl = useMemo(
    () => order?.integration?.returnUrl,
    [order?.integration?.returnUrl],
  );
  const successUrl = useMemo(
    () => order?.integration?.successUrl || order?.integration?.returnUrl,
    [order?.integration],
  );
  const failUrl = useMemo(
    () => order?.integration?.failUrl || order?.integration?.returnUrl,
    [order?.integration],
  );

  const updatePaymentInfo = useCallback(
    (
      updateDto: UpdateOrderDto,
      {
        onSuccess,
        onError,
      }: { onSuccess?: () => void; onError?: () => void } = {},
    ) => {
      update(
        { id: orderId, data: updateDto },
        {
          onSuccess: (data) => {
            apiActionOptions.onSuccess(data);
            onSuccess?.();
          },
          onError: (data: any) => {
            eventUtils.trackException({
              message: `updatePaymentInfo failed. ${
                data?.response?.data?.message || data?.message || ''
              }`,
            });
            apiActionOptions.onError(data);
            onError?.();
          },
        },
      );
    },
    [orderId, update, apiActionOptions],
  );

  const navigateReturnUrl = useCallback(() => {
    const url =
      status === OrderStatus.Completed
        ? successUrl
        : status === OrderStatus.Cancelled
        ? failUrl
        : returnUrl;
    if (url) {
      window.location.href = url;
    }
  }, [status, returnUrl, successUrl, failUrl]);

  const selectPaymentType = useCallback(
    (tradeMethod: TradeMethod) => {
      eventUtils.trackEvent({
        event: EventName.SelectPaymentType,
        message: [tradeMethod.paymentType, tradeMethod.bank]
          .filter(Boolean)
          .join(' / '),
      });
      // ecom: don't start payment on selection. Just set the payment type so the order stays New and
      // the SCI shows the card form first. The card submit (submitEcomCard) then triggers startPaymentSync.
      // No bank — ecom is bankless; sending the any-bank placeholder code makes providers reject the order.
      if (tradeMethod.paymentType === PaymentType.Ecom) {
        updatePaymentInfo({ payment: { type: tradeMethod.paymentType } });
        return;
      }
      if (order?.shop?.sciCustomTradeMethodsEnabled) {
        // manual method sync owns the cascade; block the New-status auto-effect
        // from firing a second startRequisitesCascade if no requisites are found
        startPaymentLaunched.current = true;
        startPaymentSync(
          {
            id: orderId,
            data: {
              payment: {
                type: tradeMethod.paymentType,
                bank: tradeMethod.bank,
              },
            },
          },
          {
            onSuccess: (responseOrder: Order) => {
              if (!responseOrder.requisites) {
                setStartPaymentSyncFailed(true);
              }
            },
            onError: (error: any) => {
              eventUtils.trackException({
                message: `startPaymentSync failed. ${
                  error?.response?.data?.message || error?.message || ''
                }`,
              });
              setStartPaymentSyncFailed(true);
            },
          },
        );
      } else {
        updatePaymentInfo(
          {
            payment: { type: tradeMethod.paymentType, bank: tradeMethod.bank },
          },
          {
            onSuccess: () => {
              startPaymentLaunched.current = true;
              startPayment(orderId, {
                onError: (error: any) => {
                  eventUtils.trackException({
                    message: `startPayment failed. ${
                      error?.response?.data?.message || error?.message || ''
                    }`,
                  });
                },
              });
            },
          },
        );
      }
    },
    [
      orderId,
      order?.shop?.sciCustomTradeMethodsEnabled,
      startPayment,
      updatePaymentInfo,
      startPaymentSync,
      setStartPaymentSyncFailed,
    ],
  );

  const confirmCard = useCallback(
    (formValues: PaymentFormValues, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        eventUtils.trackEvent({
          event: EventName.ConfirmCustomerDetails,
        });
      }
      updatePaymentInfo(
        { payment: omitBy(formValues, isNil) },
        customerDataBeforePayment
          ? {}
          : {
              onSuccess: () => {
                if (order?.shop?.collectCustomerReceipts) {
                  setUploadReceiptModalOpen(true);
                } else {
                  confirmPayment(orderId);
                }
              },
            },
      );
    },
    [
      confirmPayment,
      customerDataBeforePayment,
      orderId,
      updatePaymentInfo,
      order?.shop?.collectCustomerReceipts,
      setUploadReceiptModalOpen,
    ],
  );

  const actions: OrderViewActions = useMemo(
    () => ({
      cancel: {
        label: actionLabels.cancel,
        color: 'error' as const,
        onClick: () => {
          eventUtils.trackEvent({
            event: EventName.OrderCancel,
          });
          cancelMutate({ id: orderId });
        },
      },
      confirmCard: {
        label: actionLabels.confirmCard,
        color: 'primary' as const,
        disabled: isCardInfoConfirmDisabled,
        onClick: () => confirmCard(paymentFormValues),
      },
      confirmPayment: {
        label: actionLabels.confirmPayment,
        color: 'success' as const,
        onClick: () => {
          eventUtils.trackEvent({
            event: EventName.ConfirmTransfer,
          });
          updatePaymentInfo(
            {
              customerConfirmStatusDetails:
                CustomerConfirmStatusDetails.CustomerPayed,
            },
            customerDataBeforePayment || isCustomerDataConfirmed
              ? {
                  onSuccess: () => {
                    if (order?.shop?.collectCustomerReceipts) {
                      setUploadReceiptModalOpen(true);
                    } else {
                      confirmPayment(orderId);
                    }
                  },
                }
              : {},
          );
        },
      },
      backToShop: {
        color: 'tertiary' as const,
        label: actionLabels.backToShop,
        onClick: () => {
          eventUtils.trackEvent({
            event: EventName.BackToShop,
          });
          navigateReturnUrl();
        },
        hidden:
          (status === OrderStatus.Completed && !successUrl) ||
          (status === OrderStatus.Cancelled && !failUrl) ||
          !returnUrl,
      },
      selectTradeMethod: {
        label: actionLabels.selectTradeMethod,
        color: 'primary' as const,
        onClick: () => setStartPaymentSyncFailed(false),
      },
    }),
    [
      isCardInfoConfirmDisabled,
      status,
      returnUrl,
      successUrl,
      failUrl,
      cancelMutate,
      orderId,
      confirmCard,
      paymentFormValues,
      updatePaymentInfo,
      customerDataBeforePayment,
      isCustomerDataConfirmed,
      order?.shop?.collectCustomerReceipts,
      confirmPayment,
      navigateReturnUrl,
      setStartPaymentSyncFailed,
      setUploadReceiptModalOpen,
      actionLabels,
    ],
  );

  const cancelWithFeedback = useCallback(
    (feedback?: CancelFeedback) => {
      eventUtils.trackEvent({
        event: EventName.OrderCancel,
      });
      cancelMutate({ id: orderId, feedback });
    },
    [cancelMutate, orderId],
  );

  return {
    actions,
    selectPaymentType,
    confirmCard,
    confirmPayment,
    submitEcomCard,
    submitEcomConfirmCode,
    isStartPaymentSyncAPICallInProgress,
    isStartRequisitesCascadeLoading,
    isEcomSubmitInProgress,
    isCancelLoading,
    cancelWithFeedback,
  };
};
